import UnauthenticatedDemoFlow from '@/container/demo-flow/unauthenticated-demo-flow';
import {
  IS_ONBOARDING_SIGNUP_BYPASS_ENABLED,
  IS_ONBOARDING_V2_TREATMENT,
} from '@/features/flags/legacy-flags';
import { RedirectToAuthSignup, SignUpPage } from '@/features/onboarding-v2';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useKinde } from '@/hooks/useKinde';
import { SandPatternBackground } from '@heidi/growth';
import { getCountry } from '@heidi/utils/get-country';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { BookDemoCalendarStep } from './components/book-demo-calendar-step';
import { BookDemoConfirmationStep } from './components/book-demo-confirmation-step';
import {
  BookDemoEmailStep,
  EARLY_MEETING_STORAGE_KEY,
} from './components/book-demo-email-step';
import { ChoiceStep } from './components/choice-step';
import { type EarlyMeetingStep } from './early-meeting-flow.types';

export function EarlyMeetingFlow() {
  const router = useRouter();
  const { isAuthenticated } = useIsAuthenticated();
  const kindeAuth = useKinde();

  const [step, setStep] = useState<EarlyMeetingStep>('choice');
  const [email, setEmail] = useState('');

  const detectedCountry = useMemo(() => getCountry(), []);

  // Handle return from social login — only resume when the query param is present
  useEffect(() => {
    const hasBookDemoQuery = router.query.flow === 'book-demo';
    const hasResumeFlag = sessionStorage.getItem(EARLY_MEETING_STORAGE_KEY) === 'true';

    // Clean up stale flag
    if (hasResumeFlag && !hasBookDemoQuery) {
      sessionStorage.removeItem(EARLY_MEETING_STORAGE_KEY);
    }

    if (hasBookDemoQuery && isAuthenticated) {
      sessionStorage.removeItem(EARLY_MEETING_STORAGE_KEY);
      // Populate email from auth session
      const userEmail = kindeAuth.user?.email ?? kindeAuth.getUser?.()?.email ?? '';
      if (userEmail) setEmail(userEmail);
      setStep('demo-calendar');
    }
  }, [router.query.flow, isAuthenticated, kindeAuth]);

  const goToChoice = useCallback(() => setStep('choice'), []);
  const goToSignup = useCallback(() => setStep('signup'), []);
  const goToDemoEmail = useCallback(() => setStep('demo-email'), []);
  const goToCalendar = useCallback(() => setStep('demo-calendar'), []);
  const goToConfirmation = useCallback(() => setStep('demo-confirmation'), []);

  // When user chooses "Sign up free", segue into whichever signup surface is
  // appropriate. Bypass takes precedence over the V2 experiment so the
  // marketing → auth flow works regardless of V2 rollout state:
  //   - bypass on               → RedirectToAuthSignup (straight to auth)
  //   - bypass off + v2 treatment → SignUpPage (email collector)
  //   - bypass off + v2 control   → UnauthenticatedDemoFlow (legacy)
  //   - bypass off + v2 disabled  → UnauthenticatedDemoFlow (legacy)
  //
  // TODO(release-onboarding-signup-bypass): the EarlyMeetingFlow → bypass
  // cohort isn't currently counted in the bypass experiment denominator —
  // `OnboardingPage`'s `useManualExposure` excludes EarlyMeetingFlow users
  // and we don't fire our own exposure here. If we ever want to compare
  // Auth0 conversion across the marketing-direct vs EarlyMeetingFlow entry
  // paths, wire `useManualExposure({ flagKey: 'release-onboarding-signup-
  // bypass', isEnabled: …, isVisible: step === 'signup' })` here.
  if (step === 'signup') {
    if (IS_ONBOARDING_SIGNUP_BYPASS_ENABLED()) {
      return <RedirectToAuthSignup />;
    }
    if (IS_ONBOARDING_V2_TREATMENT()) {
      return <SignUpPage />;
    }
    return <UnauthenticatedDemoFlow />;
  }

  return (
    <div className="relative flex min-h-dvh w-full flex-col items-center justify-center overflow-hidden">
      <SandPatternBackground />

      {step === 'choice' && (
        <ChoiceStep onSignupClick={goToSignup} onBookDemoClick={goToDemoEmail} />
      )}

      {step === 'demo-email' && (
        <BookDemoEmailStep
          email={email}
          onEmailChange={setEmail}
          onContinue={goToCalendar}
          onBack={goToChoice}
        />
      )}

      {step === 'demo-calendar' && (
        <BookDemoCalendarStep
          email={email}
          initialCountry={detectedCountry}
          onBooked={goToConfirmation}
          onBack={goToDemoEmail}
          onSignup={goToSignup}
        />
      )}

      {step === 'demo-confirmation' && <BookDemoConfirmationStep email={email} />}
    </div>
  );
}
